/**
 * Sol carousel — shared behavior for the Sol sections' horizontal scrollers.
 *
 * Markup contract (all elements live inside the same section wrapper):
 *   [data-sol-carousel]  the horizontal scroll container (slides = children)
 *   [data-sol-dots]      optional container of <button> dots, one per slide
 *   [data-sol-prev]      optional "previous page" button
 *   [data-sol-next]      optional "next page" button
 *
 * Provides:
 *   - mouse drag-to-scroll (touch uses native scrolling)
 *   - keyboard support (arrow keys when the scroller is focused)
 *   - dot syncing with aria-current, and click-to-scroll
 *   - prev/next paging with aria-disabled state at the edges
 *   - theme editor compatibility (shopify:section:load / :unload)
 */
(function () {
  'use strict';

  if (window.SolCarousel) return;

  var DRAG_MULTIPLIER = 1.4;
  var DRAG_CLICK_THRESHOLD = 6;
  var controllers = new WeakMap();

  function scopeOf(el) {
    return el.closest('[data-sol-carousel-scope]') || el.closest('.shopify-section') || document.body;
  }

  function slideStep(carousel) {
    var first = carousel.children[0];
    if (!first) return carousel.clientWidth;
    var gap = parseFloat(getComputedStyle(carousel).columnGap) || 0;
    return first.getBoundingClientRect().width + gap;
  }

  function activeIndex(carousel) {
    var step = slideStep(carousel);
    if (step <= 0) return 0;
    return Math.min(Math.round(carousel.scrollLeft / step), carousel.children.length - 1);
  }

  function scrollToIndex(carousel, index) {
    carousel.scrollTo({ left: slideStep(carousel) * index, behavior: 'smooth' });
  }

  function setupDrag(carousel, signal) {
    var startX = 0;
    var startScroll = 0;
    var dragged = false;
    var dragging = false;

    carousel.addEventListener(
      'pointerdown',
      function (event) {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        event.preventDefault(); // stop native image drag
        dragging = true;
        dragged = false;
        startX = event.clientX;
        startScroll = carousel.scrollLeft;
      },
      { signal: signal }
    );

    window.addEventListener(
      'pointermove',
      function (event) {
        if (!dragging) return;
        var delta = event.clientX - startX;
        if (Math.abs(delta) > DRAG_CLICK_THRESHOLD) {
          dragged = true;
          carousel.classList.add('sol-dragging');
        }
        carousel.scrollLeft = startScroll - delta * DRAG_MULTIPLIER;
      },
      { signal: signal }
    );

    window.addEventListener(
      'pointerup',
      function () {
        if (!dragging) return;
        dragging = false;
        carousel.classList.remove('sol-dragging');
        // The click that belongs to this drag (if any) fires before timers,
        // so this reset never suppresses a later, genuine click.
        window.setTimeout(function () {
          dragged = false;
        }, 0);
      },
      { signal: signal }
    );

    // Suppress the click that follows a drag so links aren't triggered.
    carousel.addEventListener(
      'click',
      function (event) {
        if (!dragged) return;
        dragged = false;
        event.preventDefault();
        event.stopPropagation();
      },
      { capture: true, signal: signal }
    );
  }

  function setupKeyboard(carousel, signal) {
    if (!carousel.hasAttribute('tabindex')) carousel.setAttribute('tabindex', '0');

    carousel.addEventListener(
      'keydown',
      function (event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        if (event.target !== carousel) return;
        event.preventDefault();
        var direction = event.key === 'ArrowRight' ? 1 : -1;
        scrollToIndex(carousel, Math.max(0, activeIndex(carousel) + direction));
      },
      { signal: signal }
    );
  }

  function setupDots(carousel, dots, signal) {
    var buttons = Array.prototype.slice.call(dots.querySelectorAll('button'));
    if (!buttons.length) return;

    function sync() {
      var index = activeIndex(carousel);
      for (var i = 0; i < buttons.length; i++) {
        var active = i === index;
        buttons[i].classList.toggle('is-active', active);
        if (active) {
          buttons[i].setAttribute('aria-current', 'true');
        } else {
          buttons[i].removeAttribute('aria-current');
        }
      }
    }

    buttons.forEach(function (button, index) {
      button.addEventListener(
        'click',
        function () {
          scrollToIndex(carousel, index);
        },
        { signal: signal }
      );
    });

    var pending = false;
    carousel.addEventListener(
      'scroll',
      function () {
        if (pending) return;
        pending = true;
        window.requestAnimationFrame(function () {
          pending = false;
          sync();
        });
      },
      { passive: true, signal: signal }
    );

    sync();
  }

  function setupNav(carousel, nav, signal) {
    var items = Array.prototype.slice.call(nav.querySelectorAll('[data-sol-nav-item]'));
    if (!items.length) return;

    function highlight(index) {
      for (var i = 0; i < items.length; i++) {
        items[i].classList.toggle('on', i === index);
      }
    }

    items.forEach(function (item, index) {
      item.addEventListener(
        'mouseenter',
        function () {
          highlight(index);
        },
        { signal: signal }
      );
    });

    var pending = false;
    carousel.addEventListener(
      'scroll',
      function () {
        if (pending) return;
        pending = true;
        window.requestAnimationFrame(function () {
          pending = false;
          highlight(activeIndex(carousel));
        });
      },
      { passive: true, signal: signal }
    );
  }

  function setupArrows(carousel, prev, next, signal) {
    function maxScroll() {
      return carousel.scrollWidth - carousel.clientWidth;
    }

    function sync() {
      if (prev) prev.setAttribute('aria-disabled', carousel.scrollLeft <= 2 ? 'true' : 'false');
      if (next) next.setAttribute('aria-disabled', carousel.scrollLeft >= maxScroll() - 2 ? 'true' : 'false');
    }

    function page(direction) {
      carousel.scrollBy({ left: carousel.clientWidth * 0.8 * direction, behavior: 'smooth' });
    }

    if (prev) {
      prev.addEventListener('click', function () { page(-1); }, { signal: signal });
    }
    if (next) {
      next.addEventListener('click', function () { page(1); }, { signal: signal });
    }

    var pending = false;
    carousel.addEventListener(
      'scroll',
      function () {
        if (pending) return;
        pending = true;
        window.requestAnimationFrame(function () {
          pending = false;
          sync();
        });
      },
      { passive: true, signal: signal }
    );

    sync();
  }

  function initCarousel(carousel) {
    if (carousel.hasAttribute('data-sol-carousel-init')) return;
    carousel.setAttribute('data-sol-carousel-init', '');

    var controller = new AbortController();
    var scope = scopeOf(carousel);

    var existing = controllers.get(scope) || [];
    existing.push(controller);
    controllers.set(scope, existing);

    setupDrag(carousel, controller.signal);
    setupKeyboard(carousel, controller.signal);

    var dots = scope.querySelector('[data-sol-dots]');
    if (dots) setupDots(carousel, dots, controller.signal);

    var nav = scope.querySelector('[data-sol-nav]');
    if (nav) setupNav(carousel, nav, controller.signal);

    var prev = scope.querySelector('[data-sol-prev]');
    var next = scope.querySelector('[data-sol-next]');
    if (prev || next) setupArrows(carousel, prev, next, controller.signal);
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var carousels = scope.querySelectorAll('[data-sol-carousel]:not([data-sol-carousel-init])');
    for (var i = 0; i < carousels.length; i++) {
      initCarousel(carousels[i]);
    }
  }

  window.SolCarousel = { init: initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var scoped = controllers.get(event.target) || [];
    scoped.forEach(function (controller) {
      controller.abort();
    });
    controllers.delete(event.target);
  });
})();
